import type { AudioEngine } from './AudioEngine.js';

/** Short delivery-related notification cues. Each variant is a tiny synthesized arpeggio
 *  routed through the class's `volumeGain` / `muteGain` so the Settings → Sounds slider
 *  + mute for the "ping" layer act on it (in addition to the global master). */
export class PingSound {
  private audio: AudioEngine;
  private volumeGain: GainNode;
  private muteGain: GainNode;
  private volume = 1.0;
  private muted = false;

  constructor(audio: AudioEngine) {
    this.audio = audio;
    this.volumeGain = audio.ctx.createGain();
    this.volumeGain.gain.value = this.volume;
    this.muteGain = audio.ctx.createGain();
    this.muteGain.gain.value = 1.0;
    this.volumeGain.connect(this.muteGain).connect(audio.master);
  }

  setVolume(v: number): void {
    this.volume = clamp01(v);
    this.volumeGain.gain.setTargetAtTime(this.volume, this.audio.ctx.currentTime, 0.05);
  }

  getVolume(): number {
    return this.volume;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.muteGain.gain.setTargetAtTime(m ? 0 : 1, this.audio.ctx.currentTime, 0.05);
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** New-order ping: two-note rising arpeggio C5 → G5. */
  play(): void {
    const ctx = this.audio.ctx;
    if (ctx.state !== 'running') return;
    const now = ctx.currentTime;
    this.playNote(523.25, now, 0,    0.25, 0.22, 'sine'); // C5
    this.playNote(783.99, now, 0.12, 0.25, 0.22, 'sine'); // G5
  }

  /** Pickup confirmation: short single mid-tone blip. */
  playPickup(): void {
    const ctx = this.audio.ctx;
    if (ctx.state !== 'running') return;
    const now = ctx.currentTime;
    this.playNote(659.25, now, 0,    0.22, 0.12, 'triangle'); // E5
    this.playNote(987.77, now, 0.06, 0.22, 0.14, 'triangle'); // B5
  }

  /** Delivery success: uplifting three-note arpeggio G5 → C6 → E6. */
  playSuccess(): void {
    const ctx = this.audio.ctx;
    if (ctx.state !== 'running') return;
    const now = ctx.currentTime;
    this.playNote(783.99, now, 0,    0.28, 0.18, 'sine');     // G5
    this.playNote(1046.5, now, 0.10, 0.28, 0.18, 'sine');     // C6
    this.playNote(1318.5, now, 0.20, 0.30, 0.30, 'triangle'); // E6
  }

  /** Soft failure / negative cue: descending two-note F4 → C4. */
  playFail(): void {
    const ctx = this.audio.ctx;
    if (ctx.state !== 'running') return;
    const now = ctx.currentTime;
    this.playNote(349.23, now, 0,    0.22, 0.18, 'sawtooth'); // F4
    this.playNote(261.63, now, 0.12, 0.22, 0.22, 'sawtooth'); // C4
  }

  private playNote(
    freq: number,
    baseTime: number,
    offset: number,
    peakGain: number,
    decaySec: number,
    type: OscillatorType,
  ): void {
    const ctx = this.audio.ctx;
    const t0 = baseTime + offset;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(peakGain, t0 + 0.005); // attack
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + decaySec);

    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = freq;

    osc.connect(gain).connect(this.volumeGain);
    osc.start(t0);
    osc.stop(t0 + decaySec + 0.05);
    // GC handles disconnect after stop.
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
